import type { OutputAsset } from "@forgepoint/shared";
import { ArrowRight, Star } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MessageTimestamp } from "@/components/MessageTimestamp";
import { IndexSection } from "./IndexSection";
import { IndexRow } from "./IndexRow";

/** Where a quote sits in finalization, which also decides what a click does. */
export enum ThreadSummaryQuoteStatus {
  DRAFT = "draft",
  AWAITING_APPROVAL = "awaitingApproval",
  FINALIZED = "finalized",
  OTHER = "other",
}

/** A quote linked to the thread's opportunity or referenced by an open task. */
export interface ThreadSummaryQuote {
  quoteId: string;
  label: string;
  displayNumber?: string;
  status: ThreadSummaryQuoteStatus;
  canReview: boolean;
}

const PREVIEW_ACTION = "Preview";

const QUOTE_STATUSES: Record<
  ThreadSummaryQuoteStatus,
  { hint: string; action: string; markerClassName: string }
> = {
  [ThreadSummaryQuoteStatus.DRAFT]: {
    hint: "Draft",
    action: "Finalize",
    markerClassName: "bg-info",
  },
  [ThreadSummaryQuoteStatus.AWAITING_APPROVAL]: {
    hint: "Awaiting approval",
    action: "Review",
    markerClassName: "bg-warning",
  },
  [ThreadSummaryQuoteStatus.FINALIZED]: {
    hint: "Finalized",
    action: "Open",
    markerClassName: "bg-success",
  },
  [ThreadSummaryQuoteStatus.OTHER]: {
    hint: "Archived",
    action: "Open",
    markerClassName: "bg-border",
  },
};

const QUOTE_STATUS_ORDER: ThreadSummaryQuoteStatus[] = [
  ThreadSummaryQuoteStatus.DRAFT,
  ThreadSummaryQuoteStatus.AWAITING_APPROVAL,
  ThreadSummaryQuoteStatus.FINALIZED,
  ThreadSummaryQuoteStatus.OTHER,
];

/** Props for the ThreadSummaryPanel component. */
interface ThreadSummaryPanelProps {
  opportunityId: string | null;
  opportunityName: string | null;
  /** Whether this thread is the opportunity's primary thread. */
  isPrimaryThread: boolean;
  assets: OutputAsset[];
  quotes: ThreadSummaryQuote[];
  onOpportunityClick: () => void;
  onAssetClick: (asset: OutputAsset) => void;
  onQuoteClick: (quoteId: string) => void;
  openingQuoteId: string | null;
}

/**
 * Context panel for a thread, laid out as an editorial table of contents: the
 * linked opportunity is the hero anchor, and referenced quotes and generated
 * files form a numbered index for quick navigation. Renders flat on the page
 * surface on narrower screens (where it sits flush against the chat) and as a
 * floating card from 2xl up (where it floats in the chat's empty gutter).
 * @param props - The component props
 * @returns The thread summary panel
 */
export function ThreadSummaryPanel({
  opportunityId,
  opportunityName,
  isPrimaryThread,
  assets,
  quotes,
  onOpportunityClick,
  onAssetClick,
  onQuoteClick,
  openingQuoteId,
}: ThreadSummaryPanelProps) {
  return (
    <div className="w-full overflow-hidden 2xl:rounded-lg 2xl:border 2xl:border-border 2xl:bg-popover 2xl:shadow-lg 2xl:shadow-black/[0.06]">
      {opportunityId && opportunityName && (
        <button
          type="button"
          onClick={onOpportunityClick}
          className="group/op flex w-full items-start gap-3 rounded-md px-4 py-3 text-left transition-colors hover:bg-accent 2xl:rounded-none 2xl:bg-muted/30"
        >
          <span className="min-w-0 flex-1">
            <span className="mb-0.5 block text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Opportunity
            </span>
            <span className="flex items-start gap-1.5 text-[15px] font-semibold leading-snug text-foreground">
              <span className="min-w-0 break-words">{opportunityName}</span>
              {isPrimaryThread && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Star
                      aria-label="Primary opportunity"
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-500 animate-in zoom-in-50 spin-in-45 duration-500 motion-reduce:animate-none"
                    />
                  </TooltipTrigger>
                  <TooltipContent>Primary opportunity</TooltipContent>
                </Tooltip>
              )}
            </span>
          </span>
          <ArrowRight className="mt-4 h-4 w-4 shrink-0 -translate-x-1 text-muted-foreground opacity-0 transition-all group-hover/op:translate-x-0 group-hover/op:opacity-100" />
        </button>
      )}

      <IndexSection title="Quotes" count={quotes.length}>
        {QUOTE_STATUS_ORDER.flatMap((status) =>
          quotes.filter((quote) => quote.status === status),
        ).map((quote, i) => (
          <IndexRow
            key={quote.quoteId}
            index={i}
            onClick={() => onQuoteClick(quote.quoteId)}
            isLoading={openingQuoteId === quote.quoteId}
            markerClassName={QUOTE_STATUSES[quote.status].markerClassName}
            hint={QUOTE_STATUSES[quote.status].hint}
            action={
              quote.canReview
                ? QUOTE_STATUSES[quote.status].action
                : PREVIEW_ACTION
            }
          >
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="break-words">{quote.label}</span>
              {quote.displayNumber && (
                <span className="text-[11px] text-muted-foreground">
                  {quote.displayNumber}
                </span>
              )}
            </span>
          </IndexRow>
        ))}
      </IndexSection>

      <IndexSection
        title="Files"
        count={assets.length}
        collapsible
        defaultOpen={false}
        contentClassName="max-h-80 overflow-y-auto"
      >
        {assets.map((asset, i) => (
          <IndexRow
            key={asset.id}
            index={i}
            onClick={() => onAssetClick(asset)}
          >
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="break-words">{asset.fileName}</span>
              <MessageTimestamp timestamp={new Date(asset.createdAt)} />
            </span>
          </IndexRow>
        ))}
      </IndexSection>
    </div>
  );
}
